import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconSpinner, IconCheckmark } from '..';
import { preloadPageForRoute } from '../../util/pagePreload';

import css from './Button.module.css';

class Button extends Component {
  constructor(props) {
    super(props);
    this.state = { mounted: false };
  }

  componentDidMount() {
    this.setState({ mounted: true }); // eslint-disable-line react/no-did-mount-set-state
  }

  render() {
    const {
      children,
      className,
      rootClassName,
      spinnerClassName,
      checkmarkClassName,
      inProgress,
      ready,
      disabled,
      isFullWidth,
      dataTestId,
      isSmall,
      isXSmall,
      enforcePagePreloadFor,
      onMouseOver,
      onTouchStart,
      ...rest
    } = this.props;
    const { mounted } = this.state;
    const rootClass = rootClassName || css.root;
    const classes = classNames(rootClass, className, {
      [css.ready]: ready,
      [css.inProgress]: inProgress,
      [css.fullWidth]: isFullWidth,
      [css.small]: isSmall,
      [css.xsmall]: isXSmall,
    });

    let content;

    if (inProgress) {
      content = <IconSpinner rootClassName={spinnerClassName || css.spinner} />;
    } else if (ready) {
      content = <IconCheckmark rootClassName={checkmarkClassName || css.checkmark} />;
    } else {
      content = children;
    }

    // All buttons are disabled until the component is mounted. This
    // prevents e.g. being able to submit forms to the backend before
    // the client side is handling the submit.
    const buttonDisabled = mounted ? disabled : true;
    const handlePreload = event => {
      preloadPageForRoute(enforcePagePreloadFor);
      return event;
    };
    const handleMouseOver = event => {
      handlePreload(event);
      onMouseOver(event);
    };
    const handleTouchStart = event => {
      handlePreload(event);
      onTouchStart(event);
    };

    return (
      // eslint-disable-next-line react/button-has-type
      <button
        data-testid={dataTestId}
        className={classes}
        {...rest}
        disabled={buttonDisabled}
        onMouseOver={handleMouseOver}
        onTouchStart={handleTouchStart}
      >
        {content}
      </button>
    );
  }
}

const { node, string, bool, func } = PropTypes;

Button.defaultProps = {
  rootClassName: null,
  className: null,
  spinnerClassName: null,
  checkmarkClassName: null,
  inProgress: false,
  ready: false,
  disabled: false,
  isFullWidth: false,
  children: null,
  isAnimating: false,
  isSmall: false,
  isXSmall: false,
  enforcePagePreloadFor: null,
  onMouseOver: () => {},
  onTouchStart: () => {},
};

Button.propTypes = {
  rootClassName: string,
  className: string,
  spinnerClassName: string,
  checkmarkClassName: string,

  inProgress: bool,
  ready: bool,
  disabled: bool,
  isFullWidth: bool,
  isAnimating: bool,
  isSmall: bool,
  isXSmall: bool,
  enforcePagePreloadFor: string,
  onMouseOver: func,
  onTouchStart: func,

  children: node,
};

export default Button;

export const PrimaryButton = ({
  component: RenderedComponent,

  className,
  dataTestId,
  isAnimating,
  ...props
}) => {
  const classes = classNames(
    css.primaryButton,
    {
      [css.primaryAnimation]: isAnimating,
    },
    className
  );

  return (
    <RenderedComponent
      rootClassName={css.root}
      className={classes}
      dataTestId={dataTestId}
      {...props}
    />
  );
};

PrimaryButton.displayName = 'PrimaryButton';
PrimaryButton.defaultProps = {
  component: Button,
};

export const SecondaryButton = ({
  component: RenderedComponent,

  className,
  ...props
}) => {
  const classes = classNames(css.secondaryButton, className);

  return <RenderedComponent rootClassName={css.root} className={classes} {...props} />;
};
SecondaryButton.displayName = 'SecondaryButton';
SecondaryButton.defaultProps = {
  component: Button,
};

export const TertiaryButton = ({ component: RenderedComponent, className, ...props }) => {
  const classes = classNames(css.tertiaryButton, className);

  return <RenderedComponent rootClassName={css.root} className={classes} {...props} />;
};
TertiaryButton.displayName = 'TertiaryButton';
TertiaryButton.defaultProps = {
  component: Button,
};

export const QuaternaryButton = ({ component: RenderedComponent, isBold, className, ...props }) => {
  const classes = classNames(
    css.quaternaryButton,
    {
      [css.bold]: isBold,
    },
    className
  );

  return <RenderedComponent rootClassName={css.root} className={classes} {...props} />;
};
QuaternaryButton.displayName = 'QuaternaryButton';
QuaternaryButton.defaultProps = {
  component: Button,
};

export const QuinaryButton = ({ component: RenderedComponent, className, ...props }) => {
  const classes = classNames(css.quinaryButton, className);

  return <RenderedComponent rootClassName={css.root} className={classes} {...props} />;
};
QuinaryButton.displayName = 'QuinaryButton';
QuinaryButton.defaultProps = {
  component: Button,
};

export const InlineTextButton = props => {
  const { rootClassName } = props;
  const classes = classNames(rootClassName || css.inlineTextButtonRoot, css.inlineTextButton);

  return <Button {...props} rootClassName={classes} />;
};
InlineTextButton.displayName = 'InlineTextButton';

export const FacebookLoginButton = props => {
  const { rootClassName } = props;
  const classes = classNames(rootClassName || css.socialButtonRoot, css.facebookButton);
  return <Button {...props} rootClassName={classes} />;
};
FacebookLoginButton.displayName = 'FacebookLoginButton';

export const GoogleLoginButton = props => {
  const { rootClassName } = props;
  const classes = classNames(rootClassName || css.socialButtonRoot, css.googleButton);
  return <Button {...props} rootClassName={classes} />;
};

GoogleLoginButton.displayName = 'GoogleLoginButton';
